import { useMemo, useState, useEffect } from "react";
import { Repeat, Check, X, Loader2 } from "lucide-react";

// =====================================================================
// ProposalAcceptanceChecklist — the Proposed-state acceptance surface.
// Replaces the appointment board while a plan is Proposed. One row per
// reviewable procedure (Candidate/Proposed); the patient accepts or
// rejects each. Acceptance is per procedure, never per visit — a
// multi-visit procedure is one accept/reject unit.
//
// onConfirm(acceptedIds) uses the accept_plan contract:
//   null  → all reviewable items accepted (fast path)
//   []    → all rejected (plan routes to Rejected)
//   [ids] → explicit accepted subset (rest become Declined)
// =====================================================================

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  return `₱${Math.round(centavos / 100).toLocaleString()}`;
}
function itemLine(it) {
  const proc = it.procedure_name || "Procedure";
  const tooth = it.tooth_number ? `#${it.tooth_number}` : "";
  const surface = it.surface ? ` ${it.surface}` : "";
  return [proc, tooth, surface].filter(Boolean).join(" ").trim();
}
function visitCountOf(it) {
  const v = Array.isArray(it?.procedure_visits) ? it.procedure_visits.length : 0;
  if (v > 1) return v;
  return Number(it?.visit_count) > 1 ? Number(it.visit_count) : 1;
}

function visitsOf(it) {
  const v = Array.isArray(it?.procedure_visits) ? [...it.procedure_visits] : [];
  return v.sort((a, b) => Number(a.sequence) - Number(b.sequence));
}

export default function ProposalAcceptanceChecklist({ plan, onConfirm, busy = false }) {
  const phaseNames = plan?.phase_names ?? {};
  const reviewable = useMemo(
    () => (plan?.items ?? []).filter((it) => it.status === "Candidate" || it.status === "Proposed"),
    [plan],
  );
  const [acceptedSet, setAcceptedSet] = useState(() => new Set());
  useEffect(() => { setAcceptedSet(new Set(reviewable.map((it) => it.id))); }, [reviewable]);

  const acceptedItems = reviewable.filter((it) => acceptedSet.has(it.id));
  const acceptedTotal = acceptedItems.reduce((s, it) => s + (Number(it.fee_centavos) || 0), 0);
  const planTotal = reviewable.reduce((s, it) => s + (Number(it.fee_centavos) || 0), 0);

  function setAccept(id, on) {
    setAcceptedSet((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }
  const acceptAll = () => setAcceptedSet(new Set(reviewable.map((it) => it.id)));
  const rejectAll = () => setAcceptedSet(new Set());

  function confirm() {
    let acceptedIds;
    if (acceptedSet.size === 0) acceptedIds = [];
    else if (acceptedSet.size === reviewable.length) acceptedIds = null;
    else acceptedIds = Array.from(acceptedSet);
    onConfirm?.(acceptedIds);
  }

  if (reviewable.length === 0) {
    return (
      <div data-testid="acceptance-checklist-empty" className="text-sm text-slate-500 px-1 py-6 text-center">
        Nothing to review on this proposal.
      </div>
    );
  }
  const rejectingAll = acceptedSet.size === 0;

  return (
    <div data-testid="acceptance-checklist" className="space-y-3">
      <div className="flex gap-2">
        <button type="button" onClick={acceptAll} data-testid="acceptance-accept-all"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm hover:border-slate-400">
          <Check className="w-3.5 h-3.5" /> Accept all
        </button>
        <button type="button" onClick={rejectAll} data-testid="acceptance-reject-all"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm hover:border-slate-400">
          <X className="w-3.5 h-3.5" /> Reject all
        </button>
      </div>
      <ul className="space-y-2">
        {reviewable.map((it) => {
          const accepted = acceptedSet.has(it.id);
          const vc = visitCountOf(it);
          const visits = visitsOf(it);
          return (
            <li key={it.id} data-testid={`acceptance-row-${it.id}`}
              className={`border rounded-lg px-3 py-2.5 border-slate-200 ${accepted ? "" : "opacity-60"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${accepted ? "text-slate-900" : "text-slate-500 line-through"}`}>{itemLine(it)}</span>
                    {vc > 1 ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">
                        <Repeat className="w-2.5 h-2.5" /> {vc} visits
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{formatPHP(Number(it.fee_centavos) || 0)}</div>
                </div>
                <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden shrink-0">
                  <button type="button" onClick={() => setAccept(it.id, true)} data-testid={`acceptance-accept-${it.id}`}
                    className={`text-xs px-3 py-1.5 ${accepted ? "bg-emerald-50 text-emerald-700" : "text-slate-500"}`}>Accept</button>
                  <button type="button" onClick={() => setAccept(it.id, false)} data-testid={`acceptance-reject-${it.id}`}
                    className={`text-xs px-3 py-1.5 border-l border-slate-200 ${!accepted ? "bg-rose-50 text-rose-700" : "text-slate-500"}`}>Reject</button>
                </div>
              </div>
              {/* Which appointment each visit lands in — so the proposal
                  shows the schedule, not just a flat list. */}
              {visits.length > 0 ? (
                <div className="mt-2 pl-0.5 space-y-0.5" data-testid={`acceptance-row-${it.id}-visits`}>
                  {visits.map((v, index) => {
                    const phase = Number(v.phase_number);
                    const appt = phase >= 1 ? (phaseNames?.[String(phase)] || `Appointment ${phase}`) : "Needs appointment";
                    const visitKey = v.id || `${it.id || "item"}-visit-${v.sequence || index}`;
                    return (
                      <div key={visitKey} className="flex items-center gap-2 text-[11px] text-slate-500">
                        <span className="font-medium text-slate-600 shrink-0">Visit {v.sequence}</span>
                        {v.template_label ? <span className="truncate">{v.template_label}</span> : null}
                        <span className={`ml-auto shrink-0 ${phase >= 1 ? "text-slate-500" : "text-amber-600"}`}>{appt}</span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
        <span data-testid="acceptance-summary" className="text-sm text-slate-600">
          Accepting {acceptedItems.length} of {reviewable.length} · {formatPHP(acceptedTotal)} of {formatPHP(planTotal)}
        </span>
        <button type="button" onClick={confirm} disabled={busy} data-testid="acceptance-confirm"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {rejectingAll ? "Reject plan" : "Confirm decision"}
        </button>
      </div>
    </div>
  );
}
